'use client';

import React, { ChangeEvent, useId, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Caption } from '../Caption/Caption';
import { Checkbox } from '../Checkbox/Checkbox';
import './CheckboxGroup.css';

export interface CheckboxGroupItem<TValue> {
  value: TValue;
  label: string;
  disabled?: boolean;
}

export interface CheckboxGroupProps<TValue> {
  label?: string;
  items: CheckboxGroupItem<TValue>[];
  values?: TValue[] | null;
  defaultValues?: TValue[] | null;
  onValuesChange?: (values: TValue[] | null) => void;
  disabled?: boolean;
  required?: boolean; // Indicates at least one should be selected. Does not add native required attrs.
  orientation?: 'vertical' | 'horizontal';
  ariaLabel?: string;
  id?: string;
  caption?: React.ReactNode;
  message?: string;
  messageType?: '' | 'error' | 'success';
}

export const CheckboxGroup = React.memo(function CheckboxGroup<TValue>({
  label,
  items,
  values,
  defaultValues = null,
  onValuesChange,
  disabled = false,
  required = false,
  orientation = 'vertical',
  ariaLabel,
  id,
  caption,
  message,
  messageType = '',
}: CheckboxGroupProps<TValue>) {
  const reactId = useId();
  const groupId = id ?? `checkbox-group-${reactId}`;
  const groupLabelId = label ? `${groupId}-label` : undefined;
  const captionId = caption ? `${groupId}-caption` : undefined;
  const messageId = message ? `${groupId}-message` : undefined;

  // Normalize items to include string value for DOM and retain original value
  const normalizedItems = useMemo(
    () => items.map((i) => ({ ...i, sValue: String(i.value) })),
    [items],
  );

  const [internalValues, setInternalValues] = useState<TValue[] | null>(defaultValues);
  const selectedValues = (values ?? internalValues) as TValue[] | null;
  const selectedSet = useMemo(
    () => new Set((selectedValues ?? []).map((v) => String(v))),
    [selectedValues],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const sVal = e.target.value;
    const source = new Set(selectedSet);
    if (source.has(sVal)) {
      source.delete(sVal);
    } else {
      source.add(sVal);
    }
    const next = normalizedItems
      .filter((i) => source.has(i.sValue))
      .map((i) => i.value) as TValue[];
    const nextOrNull = next.length ? next : null;
    if (values === undefined) {
      setInternalValues(nextOrNull);
    }
    onValuesChange?.(nextOrNull);
  };

  return (
    <div
      className="checkbox-group"
      role="group"
      aria-labelledby={groupLabelId}
      aria-describedby={[captionId, messageId].filter(Boolean).join(' ') || undefined}
      aria-invalid={messageType === 'error' || undefined}
      data-orientation={orientation}
      data-disabled={disabled}
      id={groupId}
    >
      {label && (
        <span className="checkbox-group__label" id={groupLabelId}>
          {label}
          {required && <span className="checkbox-group__required">*</span>}
        </span>
      )}
      {caption && (
        <Caption>
          <span id={captionId}>{caption}</span>
        </Caption>
      )}
      <div className="checkbox-group__items">
        {normalizedItems.map((item) => (
          <div className="checkbox-group__item" key={item.sValue}>
            <Checkbox
              label={item.label}
              value={item.sValue}
              disabled={disabled || item.disabled}
              checked={selectedSet.has(item.sValue)}
              onChange={handleChange}
              ariaLabel={ariaLabel}
            />
          </div>
        ))}
      </div>
      <AnimatePresence initial={false}>
        {message && (
          <motion.div
            key="checkbox-group-message"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <span id={messageId} className="checkbox-group__message" data-type={messageType}>
              {message}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

CheckboxGroup.displayName = 'CheckboxGroup';
